import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {getReport, ReportFilterValues, ReportPayload} from '@app/services/reports';
import {getAllActiveFranchise} from '@app/services/franchise';
import KpiStrip from './components/KpiStrip';
import ReportFilters, {FranchiseOption} from './components/ReportFilters';
import {ChartCard, ReportBarChart, ReportDonutChart} from './components/ReportChart';
import useReportExport from './hooks/useReportExport';
import {reportTableStyles} from './components/tableStyles';
import {findReportBySlug} from './reportCatalogue';

/**
 * REV2 — Credit Notes & Adjustments.
 *
 * Money written back: credit-note documents (mirrored/reversed excluded) plus
 * standalone fee credits, unioned server-side with no double-count. Same shared
 * anatomy as the other money reports.
 */

const SLUG = 'credit-notes';
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({length: 5}, (_, i) => CURRENT_YEAR - i);

const money = (v: any) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2
  }).format(Number(v) || 0);

const CreditNotes = () => {
  const definition = findReportBySlug(SLUG);

  const [payload, setPayload] = useState<ReportPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [franchises, setFranchises] = useState<FranchiseOption[]>([]);

  const [filters, setFilters] = useState<ReportFilterValues>({
    mode: 'year',
    year: CURRENT_YEAR,
    period: 'full',
    page: 1,
    limit: 25
  });
  const [applied, setApplied] = useState<ReportFilterValues>(filters);

  const {exportToExcel, exporting} = useReportExport('Credit Notes');

  const load = useCallback(async (active: ReportFilterValues) => {
    setLoading(true);
    setError(null);
    const response = await getReport(SLUG, active);
    if (response && response.success) {
      setPayload(response);
    } else {
      setPayload(null);
      setError(response?.message || 'The report could not be loaded.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  useEffect(() => {
    (async () => {
      const response = await getAllActiveFranchise();
      if (response?.success === true && Array.isArray(response.collections)) {
        setFranchises(
          response.collections.map((f: any) => ({value: f.id, label: f.name}))
        );
      }
    })();
  }, []);

  const collections = payload?.collections;
  const byPeriod = collections?.series?.by_period || [];
  const bySource = collections?.series?.by_source || [];
  const detail = collections?.detail;

  const columns = [
    {
      name: 'Type',
      width: '120px',
      cell: (row: any) => (
        <span className={`badge badge-${row.source === 'Credit note' ? 'warning' : 'secondary'}`}>
          {row.source}
        </span>
      )
    },
    {name: 'Franchise', selector: (row: any) => row.franchise || '---', wrap: true},
    {name: 'Student', selector: (row: any) => row.student || '---', wrap: true},
    {name: 'Credited on', selector: (row: any) => row.credited_on || '---', width: '120px'},
    {name: 'Reference', selector: (row: any) => row.reason || '---', wrap: true},
    {name: 'Amount', width: '120px', right: true, selector: (row: any) => money(row.amount)}
  ];

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-7">
              <h1>{definition?.name || 'Credit Notes & Adjustments'}</h1>
              <p className="text-muted mb-0">{definition?.question}</p>
            </div>
            <div className="col-sm-5">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item"><Link to="/reports">Reports</Link></li>
                <li className="breadcrumb-item active">Credit Notes</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <ReportFilters
            values={filters}
            onChange={setFilters}
            onApply={() => setApplied({...filters, limit: applied.limit, page: 1})}
            onReset={(defaults) => {
              setFilters(defaults);
              setApplied(defaults);
            }}
            onExport={() => exportToExcel(payload, SLUG, applied)}
            exporting={exporting}
            franchises={franchises}
            years={YEARS}
            intervalLabel={collections?.filters_applied?.interval_label}
            show={{franchise: true, student: true}}
            disabled={loading}
          />

          {error && (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle mr-2" />
              {error}
            </div>
          )}

          <KpiStrip kpis={collections?.kpis || []} loading={loading} />

          <div className="row">
            <div className="col-lg-7">
              <ChartCard
                title="Credits over time"
                subtitle={
                  collections?.filters_applied?.interval_label
                    ? `${collections.filters_applied.interval_label} totals`
                    : ' '
                }
                loading={loading}
                isEmpty={!byPeriod.length}>
                <ReportBarChart data={byPeriod} xKey="bucket" yKey="value" colour="#B04A5A" />
              </ChartCard>
            </div>
            <div className="col-lg-5">
              <ChartCard
                title="By type"
                subtitle="Credit notes vs fee credits"
                loading={loading}
                isEmpty={!bySource.length}>
                <ReportDonutChart data={bySource} nameKey="source" valueKey="value" />
              </ChartCard>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3 className="card-title text-capitalize">Adjustments</h3>
            </div>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                customStyles={reportTableStyles}
                data={detail?.rows || []}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={detail?.total || 0}
                paginationDefaultPage={applied.page || 1}
                paginationPerPage={applied.limit || 25}
                paginationRowsPerPageOptions={[25, 50, 100]}
                onChangePage={(page) => setApplied({...applied, page})}
                onChangeRowsPerPage={(limit, page) =>
                  setApplied({...applied, limit, page})
                }
                noDataComponent={
                  <div className="text-muted py-4">No credits or adjustments match these filters.</div>
                }
                highlightOnHover
                dense
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CreditNotes;
